import React, { useLayoutEffect, useRef } from "react";

/**
 * Roving tabindex for a list of rows.
 *
 * A designer list can hold hundreds of rows; giving each one `tabIndex={0}` would turn a panel into
 * dozens of Tab stops. Instead the whole list is ONE stop — the selected row, or the first one when
 * nothing is selected — and the arrows move between rows once focus is inside. This is the
 * WAI-ARIA listbox/tree practice: the arrows do not wrap (a long list's end is a real edge), Home
 * and End jump to the first and last row.
 *
 * The hook is the ROW's half: every row registers itself with the controller of its group
 * container, so a list's rows do not have to be rendered by one component — screens, folders and
 * their nested folders all register into the same group. The controller owns `tabindex`, one
 * delegated `keydown` and one `focusin` listener per group, and one MutationObserver that notices
 * rows appearing, leaving or being hidden (a folder collapsing).
 *
 * ── Which container is the group ──────────────────────────────────────────────────────────────
 * `findRovingGroupContainer` walks up from the row and takes the first of:
 *   1. an ancestor carrying `data-kuika-roving-group` — the explicit boundary a caller can draw;
 *   2. a KuikaSelectionIndicator host (an ancestor whose first element child is the indicator) —
 *      the list already declares "these rows share one selection", which is exactly one tab stop;
 *   3. otherwise the row's own parent, lifted out of the plumbing that only NESTS rows
 *      (rc-collapse panels, react-beautiful-dnd droppables), so a folder's rows stay in their
 *      tree's group instead of becoming a stop of their own.
 *
 * ── Timing ────────────────────────────────────────────────────────────────────────────────────
 * A group recomputes once a MICROTASK after anything changes, so a list that mounts its rows one by
 * one still writes `tabindex` once, before the frame is painted. A test therefore flushes one
 * microtask after a render (two after a change the MutationObserver reports, whose records are
 * delivered in a microtask of their own).
 */

/** Put this on a list container to say "everything under me is one tab stop". */
export const ROVING_GROUP_ATTRIBUTE: string = "data-kuika-roving-group";

/** Marks a registered row, so a group can read its own rows back in DOM order. */
export const ROVING_ITEM_ATTRIBUTE: string = "data-kuika-roving-item";

/**
 * KuikaSelectionIndicator's class (`components/ui-kit/kuika-selection-indicator`). Spelled out
 * instead of imported: that component already imports from this folder, and a shared hook must not
 * depend on a component.
 */
const SELECTION_INDICATOR_CLASS: string = "kuika-selection-indicator";

/**
 * Elements that exist only to nest rows. A row inside one of them belongs to the list the wrapper
 * itself sits in, not to the wrapper.
 */
const LIST_PLUMBING_SELECTOR: string =
  ".rc-collapse, .rc-collapse-item, .rc-collapse-content, .rc-collapse-content-box, [data-rbd-droppable-id]";

/**
 * `class` covers the case that matters — rc-collapse hides a closed folder's content with
 * `rc-collapse-content-hidden`, and a row hidden under it can no longer be the tab stop. `style` is
 * deliberately NOT watched: react-beautiful-dnd rewrites a dragged row's inline transform every
 * frame, and nothing in the kit hides a row inline.
 */
const WATCHED_ATTRIBUTES: string[] = ["class", "hidden", "aria-disabled", "disabled"];

const TAB_INDEX_ATTRIBUTE: string = "tabindex";
const TAB_STOP: string = "0";
const NOT_TAB_STOP: string = "-1";

export interface IRovingFocusOptions {
  /** Off for a row the caller gives its own `tabIndex`, and for a row that does nothing. */
  enabled: boolean;
  /** The list's current row: it is the group's tab stop until the user focuses another one. */
  selected: boolean;
}

interface IRovingRowState {
  selected: boolean;
  /** The last `tabindex` this group wrote, so it only takes back what it put there. */
  writtenTabIndex: string | null;
}

/**
 * Whether the row is drawn at all. A row in a closed folder is in the DOM but cannot take focus, so
 * it is neither a tab stop nor an arrow step. Where the browser cannot answer (`checkVisibility` is
 * Chrome 105+/Safari 17.4+, and jsdom has none), the display chain is walked instead.
 */
const isRendered = (element: HTMLElement): boolean => {
  if (typeof element.checkVisibility === "function") {
    return element.checkVisibility();
  }

  let node: HTMLElement | null = element;

  while (node !== null) {
    if (node.hidden) {
      return false;
    }

    const style: CSSStyleDeclaration = window.getComputedStyle(node);

    if (style.display === "none" || style.visibility === "hidden") {
      return false;
    }

    node = node.parentElement;
  }

  return true;
};

/** Read from the DOM, so a caller that only sets `aria-disabled` is understood without a prop. */
const isDisabled = (element: HTMLElement): boolean =>
  element.getAttribute("aria-disabled") === "true" || element.hasAttribute("disabled");

/** A native button or link is activated by the browser; a synthetic click would fire it twice. */
const isNativelyActivated = (element: HTMLElement): boolean =>
  element instanceof HTMLButtonElement || (element instanceof HTMLAnchorElement && element.hasAttribute("href"));

export const findRovingGroupContainer = (row: HTMLElement): HTMLElement | null => {
  let ancestor: HTMLElement | null = row.parentElement;

  while (ancestor !== null) {
    if (ancestor.hasAttribute(ROVING_GROUP_ATTRIBUTE)) {
      return ancestor;
    }

    const firstChild: Element | null = ancestor.firstElementChild;

    if (firstChild !== null && firstChild.classList.contains(SELECTION_INDICATOR_CLASS)) {
      return ancestor;
    }

    ancestor = ancestor.parentElement;
  }

  let container: HTMLElement | null = row.parentElement;

  while (container !== null && container.parentElement !== null && container.matches(LIST_PLUMBING_SELECTOR)) {
    container = container.parentElement;
  }

  return container;
};

/** One per list container: it owns every `tabindex` in the group and the group's key handling. */
class RovingFocusGroup {
  private readonly container: HTMLElement;
  private readonly rows: Map<HTMLElement, IRovingRowState> = new Map<HTMLElement, IRovingRowState>();
  /** The row the user last focused. It stays the stop until the list selects another row. */
  private activeRow: HTMLElement | null = null;
  private observer: MutationObserver | null = null;
  private isRecomputeScheduled: boolean = false;

  public constructor(container: HTMLElement) {
    this.container = container;
    container.addEventListener("keydown", this.handleKeyDown);
    container.addEventListener("focusin", this.handleFocusIn);

    if (typeof MutationObserver === "function") {
      this.observer = new MutationObserver(this.scheduleRecompute);
      this.observer.observe(container, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: WATCHED_ATTRIBUTES
      });
    }
  }

  /** The row joins unselected; the hook pushes its selection in the same commit. */
  public register(row: HTMLElement): void {
    this.rows.set(row, { selected: false, writtenTabIndex: null });
    row.setAttribute(ROVING_ITEM_ATTRIBUTE, "");
    this.scheduleRecompute();
  }

  public setSelected(row: HTMLElement, selected: boolean): void {
    const state: IRovingRowState | undefined = this.rows.get(row);

    if (state === undefined || state.selected === selected) {
      return;
    }

    state.selected = selected;

    // The stop follows the list: a row focused earlier stops being the entry point once the list
    // has selected another one.
    if (selected) {
      this.activeRow = null;
    }

    this.scheduleRecompute();
  }

  public unregister(row: HTMLElement): void {
    const state: IRovingRowState | undefined = this.rows.get(row);

    if (state === undefined) {
      return;
    }

    this.rows.delete(row);
    row.removeAttribute(ROVING_ITEM_ATTRIBUTE);

    // Only take back the value this group wrote: a row that left because its caller took focus over
    // already carries React's own `tabIndex`.
    if (state.writtenTabIndex !== null && row.getAttribute(TAB_INDEX_ATTRIBUTE) === state.writtenTabIndex) {
      row.removeAttribute(TAB_INDEX_ATTRIBUTE);
    }

    if (this.activeRow === row) {
      this.activeRow = null;
    }

    if (this.rows.size === 0) {
      this.dispose();

      return;
    }

    this.scheduleRecompute();
  }

  private dispose(): void {
    this.container.removeEventListener("keydown", this.handleKeyDown);
    this.container.removeEventListener("focusin", this.handleFocusIn);
    this.observer?.disconnect();
    this.observer = null;
    releaseGroup(this.container);
  }

  /** One recompute a microtask: a list mounts its rows one by one, and they all land in one commit. */
  private readonly scheduleRecompute = (): void => {
    if (this.isRecomputeScheduled) {
      return;
    }

    this.isRecomputeScheduled = true;
    queueMicrotask((): void => {
      this.isRecomputeScheduled = false;
      this.recompute();
    });
  };

  private recompute(): void {
    const ordered: HTMLElement[] = this.orderedRows();
    const stop: HTMLElement | null = this.resolveTabStop(ordered);

    ordered.forEach((row: HTMLElement): void => {
      this.writeTabIndex(row, row === stop ? TAB_STOP : NOT_TAB_STOP);
    });
  }

  /** The group's rows in DOM order. Rows of a nested group are filtered out by membership. */
  private orderedRows(): HTMLElement[] {
    const candidates: NodeListOf<HTMLElement> = this.container.querySelectorAll<HTMLElement>(
      `[${ROVING_ITEM_ATTRIBUTE}]`
    );

    return Array.from(candidates).filter((row: HTMLElement): boolean => this.rows.has(row));
  }

  private resolveTabStop(ordered: HTMLElement[]): HTMLElement | null {
    if (this.activeRow !== null && this.isNavigable(this.activeRow)) {
      return this.activeRow;
    }

    const selected: HTMLElement | undefined = ordered.find(
      (row: HTMLElement): boolean => this.rows.get(row)?.selected === true && this.isNavigable(row)
    );

    if (selected !== undefined) {
      return selected;
    }

    return ordered.find((row: HTMLElement): boolean => this.isNavigable(row)) ?? null;
  }

  private isNavigable(row: HTMLElement): boolean {
    return this.rows.has(row) && !isDisabled(row) && isRendered(row);
  }

  private writeTabIndex(row: HTMLElement, value: string): void {
    const state: IRovingRowState | undefined = this.rows.get(row);

    if (state === undefined) {
      return;
    }

    state.writtenTabIndex = value;

    if (row.getAttribute(TAB_INDEX_ATTRIBUTE) !== value) {
      row.setAttribute(TAB_INDEX_ATTRIBUTE, value);
    }
  }

  private readonly handleFocusIn = (event: FocusEvent): void => {
    const target: EventTarget | null = event.target;

    if (!(target instanceof HTMLElement)) {
      return;
    }

    const row: HTMLElement | null = target.closest<HTMLElement>(`[${ROVING_ITEM_ATTRIBUTE}]`);

    if (row === null || !this.rows.has(row) || this.activeRow === row) {
      return;
    }

    this.activeRow = row;
    this.scheduleRecompute();
  };

  private readonly handleKeyDown = (event: KeyboardEvent): void => {
    // `defaultPrevented` is what keeps react-beautiful-dnd's keyboard drag intact: its sensor sees
    // the same Space first (window, capture phase) and lifts the row.
    if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) {
      return;
    }

    const target: EventTarget | null = event.target;

    // Only the row itself. A key pressed in a rename input, or on one of the row's own action
    // buttons, belongs to that control.
    if (!(target instanceof HTMLElement) || !this.rows.has(target)) {
      return;
    }

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      this.moveByStep(target, event.key === "ArrowDown" ? 1 : -1, event);

      return;
    }

    if (event.key === "Home" || event.key === "End") {
      this.moveToEdge(event.key === "Home", event);

      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      this.activate(target, event);
    }
  };

  /** No wrap: the first and last rows are the ends of the list, as in a listbox or a tree. */
  private moveByStep(from: HTMLElement, step: number, event: KeyboardEvent): void {
    const navigable: HTMLElement[] = this.navigableRows();
    const index: number = navigable.indexOf(from);

    if (index === -1) {
      return;
    }

    this.moveFocus(navigable[index + step], event);
  }

  private moveToEdge(isFirst: boolean, event: KeyboardEvent): void {
    const navigable: HTMLElement[] = this.navigableRows();

    this.moveFocus(isFirst ? navigable[0] : navigable[navigable.length - 1], event);
  }

  private navigableRows(): HTMLElement[] {
    return this.orderedRows().filter((row: HTMLElement): boolean => this.isNavigable(row));
  }

  private moveFocus(next: HTMLElement | undefined, event: KeyboardEvent): void {
    if (next === undefined) {
      return;
    }

    event.preventDefault();
    // Set here as well as in `focusin`: a window that is not focused takes the tab stop over anyway.
    this.activeRow = next;
    next.focus();
    this.scheduleRecompute();
  }

  private activate(row: HTMLElement, event: KeyboardEvent): void {
    if (isDisabled(row) || isNativelyActivated(row)) {
      return;
    }

    // Before the repeat check: a held Space must not scroll the panel either.
    event.preventDefault();

    if (event.repeat) {
      return;
    }

    row.click();
  }
}

const groupsByContainer: WeakMap<HTMLElement, RovingFocusGroup> = new WeakMap<HTMLElement, RovingFocusGroup>();

const acquireGroup = (container: HTMLElement): RovingFocusGroup => {
  const existing: RovingFocusGroup | undefined = groupsByContainer.get(container);

  if (existing !== undefined) {
    return existing;
  }

  const created: RovingFocusGroup = new RovingFocusGroup(container);
  groupsByContainer.set(container, created);

  return created;
};

const releaseGroup = (container: HTMLElement): void => {
  groupsByContainer.delete(container);
};

/**
 * Makes one row part of its list's single tab stop. The row element must be focusable-by-attribute
 * only through this hook: do not also render a `tabIndex` on it.
 */
const useRovingFocus = (rowRef: React.RefObject<HTMLElement>, { enabled, selected }: IRovingFocusOptions): void => {
  const groupRef: React.MutableRefObject<RovingFocusGroup | null> = useRef<RovingFocusGroup | null>(null);

  useLayoutEffect((): (() => void) | undefined => {
    const row: HTMLElement | null = rowRef.current;

    if (!enabled || row === null) {
      return undefined;
    }

    const container: HTMLElement | null = findRovingGroupContainer(row);

    if (container === null) {
      return undefined;
    }

    const group: RovingFocusGroup = acquireGroup(container);
    groupRef.current = group;
    group.register(row);

    return (): void => {
      groupRef.current = null;
      group.unregister(row);
    };
  }, [rowRef, enabled]);

  // Separate from the registration on purpose: a selection change must not drop the row out of its
  // group and put it back. Declared after it, so a freshly registered row gets its selection in the
  // same commit — before the group's first recompute, which runs a microtask later.
  useLayoutEffect((): void => {
    const row: HTMLElement | null = rowRef.current;
    const group: RovingFocusGroup | null = groupRef.current;

    if (row === null || group === null) {
      return;
    }

    group.setSelected(row, selected);
  }, [rowRef, enabled, selected]);
};

export default useRovingFocus;
